/**
 * Context shared by every subcomponent under `/s/:sessionId` (Lobby, PlaySurface, InstanceHost, Results, and
 * the voice/presenter integrations built by other tasks). `view` is guaranteed non-null: `SessionPage` only
 * renders this provider's children once the first `welcome`/`state` frame has arrived.
 */
import { createContext, useContext } from 'react';
import type { ClientView, SelfInfo } from '@momentum/shared';
import type { ClosedInfo, ClientStatus, SessionClient } from './client.ts';
import type { StoredCredentials } from './credentials.ts';

export interface SessionContextValue {
  sessionId: string;
  client: SessionClient;
  view: ClientView;
  self: SelfInfo | null;
  status: ClientStatus;
  serverTimeOffset: number;
  closed: ClosedInfo | null;
  credentials: StoredCredentials;
  /** From `matchMedia('(prefers-reduced-motion: reduce)')`, kept live. */
  reducedMotion: boolean;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSessionContext must be used within a SessionContext.Provider (see SessionPage.tsx)');
  return ctx;
}
